import React from 'react'
import classnames from 'classnames'
import type { DetailedTariffItem } from '../../../apis/types'
import CopyableValue from '../../../components/CopyableValue'
import { formatUtilityTypeLabel } from './TariffChips'
import { TariffPageHeader } from './TariffPageHeader'

const HeaderField: React.FC<{
    label: string
    children: React.ReactNode
    className?: string
    valueClassName?: string
}> = ({ label, children, className, valueClassName }) => (
    <div className={className}>
        <div className="text-xs font-medium uppercase leading-4 text-slate-500 dark:text-slate-400">
            {label}
        </div>
        <div className={classnames('mt-0.5 flex min-h-6', valueClassName ?? 'items-center')}>
            {children}
        </div>
    </div>
)

export const TariffDetailHeader: React.FC<{
    backLink: React.ReactNode
    tariff: DetailedTariffItem
    utilityLabel: string
    detailActions: React.ReactNode
    exportAction?: React.ReactNode
}> = ({ backLink, tariff, utilityLabel, detailActions, exportAction }) => {
    return (
        <div className="sticky top-0 z-20 -mx-1 space-y-3 border-b border-slate-200 bg-white px-1 py-3 dark:border-slate-700 dark:bg-slate-900">
            <TariffPageHeader className="mb-0" title={backLink} />
            <div className="flex w-full items-end justify-between gap-3">
                <div className="flex min-w-0 flex-1 flex-wrap items-end gap-x-6 gap-y-2">
                    <HeaderField label="Schedule">
                        <h1 className="m-0 font-mono text-xl font-semibold leading-6 text-slate-900 dark:text-white">
                            {tariff.schedule}
                        </h1>
                    </HeaderField>
                    {tariff.name != null && tariff.name !== '' ? (
                        <HeaderField label="Name" className="min-w-0 max-w-xl">
                            <h2 className="m-0 truncate text-xl font-semibold leading-6 text-slate-900 dark:text-white">
                                {tariff.name}
                            </h2>
                        </HeaderField>
                    ) : null}
                    {utilityLabel !== '' ? (
                        <HeaderField label="Utility" className="min-w-0 max-w-sm">
                            <div className="truncate text-sm leading-6 text-slate-600 dark:text-slate-300">
                                {utilityLabel}
                            </div>
                        </HeaderField>
                    ) : null}
                    <HeaderField label="Tariff cuid">
                        <CopyableValue value={tariff.cuid} label="tariff cuid" truncate={false} />
                    </HeaderField>
                    <HeaderField
                        label="Internal notes"
                        className="min-w-[12rem] max-w-md"
                        valueClassName="items-start"
                    >
                        {tariff.modifiedDescription != null && tariff.modifiedDescription !== '' ? (
                            <div className="whitespace-pre-wrap text-sm leading-5 text-slate-900 dark:text-slate-100">
                                {tariff.modifiedDescription}
                            </div>
                        ) : (
                            <span className="text-sm leading-5 text-slate-400 dark:text-slate-500">—</span>
                        )}
                    </HeaderField>
                </div>
                {detailActions != null || exportAction != null ? (
                    <div className="ml-auto mr-12 flex shrink-0 items-center justify-end gap-2 whitespace-nowrap">
                        {exportAction}
                        {detailActions}
                    </div>
                ) : null}
            </div>
        </div>
    )
}

export const formatTariffUtilityLabel = (
    name: string | null | undefined,
    shortName: string | null | undefined,
    utilityType: string | null | undefined
): string => {
    const typeLabel = formatUtilityTypeLabel(utilityType)
    const trimmedName = name?.trim() ?? ''
    const trimmedShort = shortName?.trim() ?? ''
    const shortAlreadyInName =
        trimmedShort !== '' &&
        trimmedName.toLowerCase().includes(trimmedShort.toLowerCase())
    const parts = [
        trimmedName !== '' ? trimmedName : trimmedShort !== '' ? trimmedShort : null,
        shortAlreadyInName || trimmedShort === '' || trimmedName === '' ? null : trimmedShort,
        typeLabel !== '' ? typeLabel : null,
    ].filter((part): part is string => part != null && part !== '')
    return parts.join(' · ')
}
